'use client';

import { lffLink } from '@/constants';
import { useIntersectionObserver } from '@/lib/hooks';
import { cn, isMobileDevice, isSafari } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import H4 from '../ui/H4';
import Link from '../ui/Link';
import Header from './Header';
import Video from './Video';

const MainHero = () => {
    const t = useTranslations('About');
    const [heroRef, isObserved] = useIntersectionObserver({ threshold: 0.1 });
    const [aboutSectionRef, isAboutSectionObserved] = useIntersectionObserver({ threshold: 0.1 });

    const [isMobile, setIsMobile] = useState(false);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        if (isObserved !== undefined) {
            setIsVisible(isObserved);
        }
    }, [isObserved]);

    useEffect(() => {
        if (isMobileDevice() || isSafari()) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, []);

    return (
        <>
            <Header color={isVisible ? 'white' : 'black'} />

            <div ref={heroRef} className="min-h-screen w-full bg-gray-300 relative">
                <Image src="/images/hero-bg.webp" alt="Background" fill />

                <Image
                    src="/images/shadow-1.png"
                    alt="Shadow 1"
                    width={880}
                    height={710}
                    className="absolute top-1/6 left-0 shadow-animate-1 hidden lg:block"
                />
                <Image
                    src="/images/shadow-1.png"
                    alt="Shadow 1"
                    width={410}
                    height={300}
                    className="lg:hidden absolute top-1/6 -left-1/2 shadow-animate-1"
                />
                <Image
                    src="/images/shadow-2.png"
                    alt="Shadow 2"
                    width={360}
                    height={400}
                    className="absolute bottom-1/3 right-0 shadow-animate-2 hidden lg:block"
                />
                <Image
                    src="/images/shadow-2.png"
                    alt="Shadow 2"
                    width={310}
                    height={250}
                    className="lg:hidden absolute bottom-1/4 -right-1/4 shadow-animate-2"
                />
                <>
                    <div className="lg:h-[calc(100vh-164px)] h-[calc(100vh-118px)] mt-[118px] lg:mt-[150px] flex justify-center items-center relative mx-auto max-w-[1000px] w-auto">
                        {isMobile ? (
                            <Image
                                className="object-contain object-center"
                                src="/images/logo-color.webp"
                                alt="Video logo"
                                fill
                                priority
                            />
                        ) : (
                            <Video
                                src={'/videos/hero.webm'}
                                poster="/images/logo-color.webp"
                                className="overflow-hidden w-full h-full object-contain object-center"
                            />
                        )}
                    </div>

                    <div
                        className="lg:mx-10 mx-5 lg:my-24 my-7 lg:h-[480px] h-[160px] relative overflow-hidden"
                        ref={aboutSectionRef}
                    >
                        <Link href={lffLink}>
                            <>
                                <Image
                                    className={cn(
                                        'translate-y-20 transition-transform duration-1000 object-cover object-center',
                                        isAboutSectionObserved && 'translate-y-0',
                                    )}
                                    src="/images/about.webp"
                                    alt="About"
                                    fill
                                />
                                <H4
                                    className={cn(
                                        'lg:max-w-[260px] max-w-[160px] absolute lg:bottom-5 lg:right-5 right-3 bottom-3 text-black max-lg:text-h6 translate-y-10 transition-all duration-0 delay-0 hover:text-blue-200 drop-shadow-md',
                                        isAboutSectionObserved && 'translate-y-0',
                                    )}
                                >
                                    {t('title')}
                                </H4>
                            </>
                        </Link>
                    </div>
                </>
            </div>
        </>
    );
};

export default MainHero;
